import { Course, Semester } from '../types/grades';
import { University } from '../types/universities';

interface GPAStats {
  effectiveGPA: number;
  totalCredits: number;
  effectiveCredits: number;
  specialGradeCredits: number;
}

const findRetakenCourses = (semesters: Semester[]): Map<string, Course[]> => {
  const courseMap = new Map<string, Course[]>();
  
  semesters.forEach(semester => {
    semester.courses.forEach(course => {
      if (course.code) {
        const existing = courseMap.get(course.code) || [];
        courseMap.set(course.code, [...existing, { ...course, originalSemesterId: semester.id }]);
      }
    });
  });
  
  return courseMap;
};

const getLatestGrade = (courses: Course[]): Course => {
  return courses.reduce((latest, current) => {
    if (!latest) return current;
    const latestSemesterId = parseInt(latest.originalSemesterId || '0');
    const currentSemesterId = parseInt(current.originalSemesterId || '0');
    return currentSemesterId > latestSemesterId ? current : latest;
  });
};

export const getGPV = (grade: string, university: University): number => {
  if (!grade) return 0;
  const gradeScale = university.gradeScale.find(g => g.grade === grade);
  return gradeScale?.points || 0;
};

export const calculateGPA = (courses: Course[], university: University): GPAStats => {
  if (!courses.length || !university) return 0;

  const validCourses = courses.filter(course => 
    course.credits > 0 && course.grade && !course.isSpecialGrade
  );
  
  const specialCourses = courses.filter(course => 
    course.credits > 0 && course.grade && course.isSpecialGrade
  );

  const totalPoints = validCourses.reduce((sum, course) => {
    return sum + (course.credits * getGPV(course.grade, university));
  }, 0);

  const totalCredits = validCourses.reduce((sum, course) => sum + course.credits, 0);
  const specialCredits = specialCourses.reduce((sum, course) => sum + course.credits, 0);

  return {
    effectiveGPA: totalCredits > 0 ? Number((totalPoints / totalCredits).toFixed(2)) : 0,
    totalCredits: totalCredits + specialCredits,
    effectiveCredits: totalCredits,
    specialGradeCredits: specialCredits
  };
};

export const calculateCumulativeGPA = (semesters: Semester[], university: University): number => {
  if (!semesters.length || !university) return 0;

  // Get all courses and identify retakes
  const courseMap = findRetakenCourses(semesters);
  
  // Filter to only valid courses and use latest grade for retakes
  const validCourses = Array.from(courseMap.values())
    .map(courses => getLatestGrade(courses))
    .filter(course => course.credits > 0 && course.grade);

  if (!validCourses.length) return 0;

  const totalPoints = validCourses.reduce((sum, course) => {
    return sum + (course.credits * getGPV(course.grade, university));
  }, 0);

  const totalCredits = validCourses.reduce((sum, course) => sum + course.credits, 0);

  return totalCredits > 0 ? Number((totalPoints / totalCredits).toFixed(2)) : 0;
};